import apiClient from './axios';
import { ApiResponse } from '../types';

/** Which app the money came in through. */
export type FinanceApp = 'consumer' | 'business' | 'commerce';
/** Fees to the companies PayIQ runs on, or money PayIQ chooses to pay out. */
export type CostKind = 'provider' | 'payouts';

export interface RevenueLine {
  key: string;
  app: FinanceApp;
  appLabel: string;
  label: string;
  note: string;
  quantity: number;
  amount: number;
}

export interface CostLine {
  key: string;
  kind: CostKind;
  provider: string;
  providerLabel: string;
  /** Set on a per-use product, which is priced from a rate. */
  product: string | null;
  label: string;
  note: string | null;
  unit: string | null;
  quantity: number;
  counts: string;
  amount: number;
  /** Uses with no price set, so costed at nothing. */
  unpriced: number;
}

export interface FinanceReport {
  from: string;
  to: string;
  ngnPerUsd: number;
  fxConfigured: boolean;
  totals: {
    revenue: number;
    costs: number;
    providerFees: number;
    payouts: number;
    fixed: number;
    net: number;
  };
  months: { month: string; revenue: number; costs: number; net: number }[];
  revenue: {
    byApp: { app: FinanceApp; label: string; amount: number }[];
    lines: RevenueLine[];
  };
  costs: {
    byProvider: { provider: string; label: string; kind: CostKind; amount: number }[];
    lines: CostLine[];
    fixed: {
      id: string;
      name: string;
      vendor: string | null;
      category: string;
      cadence: Cadence;
      amount: number;
    }[];
  };
  unpriced: {
    provider: string;
    providerLabel: string;
    product: string;
    label: string;
    unit: string | null;
    quantity: number;
  }[];
  warnings: string[];
}

export interface Rate {
  id: string;
  unitCost: number;
  currency: Currency;
  /** null: applies to every use ever made. */
  effectiveFrom: string | null;
  note: string | null;
  createdAt: string;
}

export interface MeteredProduct {
  provider: string;
  providerLabel: string;
  product: string;
  label: string;
  unit: string;
  unitSize: number;
  current: Rate | null;
  history: Rate[];
}

export type Currency = 'NGN' | 'USD';
export type Cadence = 'monthly' | 'yearly' | 'once';
export type CostCategory =
  | 'servers'
  | 'software'
  | 'staff'
  | 'office'
  | 'marketing'
  | 'compliance'
  | 'other';

export interface FixedCost {
  id: string;
  name: string;
  vendor: string | null;
  category: CostCategory;
  amount: number;
  currency: Currency;
  cadence: Cadence;
  startsOn: string;
  endsOn: string | null;
  note: string | null;
}

export type FixedCostInput = Omit<FixedCost, 'id'>;

const unwrap = async <T>(p: Promise<{ data: ApiResponse<T> }>) => (await p).data.data;

export const financeApi = {
  report: (from: string, to: string) =>
    unwrap(apiClient.get<ApiResponse<FinanceReport>>('/admin/finance/report', { params: { from, to } })),

  rates: () =>
    unwrap(
      apiClient.get<ApiResponse<{ products: MeteredProduct[]; ngnPerUsd: number }>>('/admin/finance/rates'),
    ),
  setRate: (body: {
    provider: string;
    product: string;
    unitCost: number;
    currency: Currency;
    effectiveFrom?: string;
    note?: string;
  }) => unwrap(apiClient.post<ApiResponse<Rate>>('/admin/finance/rates', body)),
  removeRate: (id: string) => unwrap(apiClient.delete<ApiResponse<{ removed: boolean }>>(`/admin/finance/rates/${id}`)),

  fixedCosts: () => unwrap(apiClient.get<ApiResponse<FixedCost[]>>('/admin/finance/fixed-costs')),
  createFixedCost: (body: FixedCostInput) =>
    unwrap(apiClient.post<ApiResponse<FixedCost>>('/admin/finance/fixed-costs', body)),
  updateFixedCost: (id: string, body: Partial<FixedCostInput>) =>
    unwrap(apiClient.patch<ApiResponse<FixedCost>>(`/admin/finance/fixed-costs/${id}`, body)),
  removeFixedCost: (id: string) =>
    unwrap(apiClient.delete<ApiResponse<{ removed: boolean }>>(`/admin/finance/fixed-costs/${id}`)),
};
